/**
 * A value axis's minimum, maximum and major unit the way Office picks them
 * when the chart leaves them automatic — a line-for-line port of
 * App\Services\Docx\Studio\Presentation\Charts\AxisScale, so a chart the
 * admin edits on the canvas scales exactly as the server (and Word) will
 * scale it once saved. Both sides run the same vectors
 * (tests/Fixtures/charts/axis-scale-vectors.json): every value axis of the
 * prod malls' charts.
 */

/** Axis length per major interval, at least, in tick-label font sizes. */
export const DENSITY = 1.1

/** Office never draws more major intervals than this on an automatic axis. */
export const MAX_INTERVALS = 10

const STEPS = [1, 2, 5]

const floorTo = (value, unit) => Math.floor(value / unit + 1e-9) * unit

const ceilTo = (value, unit) => Math.ceil(value / unit - 1e-9) * unit

/** Strip binary noise (0.30000000000000004 → 0.3), as PHP's sprintf('%.12g'). */
export const clean = (value) => Number(value.toPrecision(12))

function provisional(dataMin, dataMax, fixedMin, fixedMax) {
  const spread = dataMax - dataMin
  let low
  if (fixedMin !== null) low = fixedMin
  else if (dataMin >= 0) low = dataMax > 0 && spread / dataMax > 1 / 6 ? 0 : Math.max(0, dataMin - spread / 2)
  else low = dataMin - 0.05 * spread

  let high
  if (fixedMax !== null) high = fixedMax
  else if (dataMax <= 0) high = dataMin < 0 && spread / Math.abs(dataMin) > 1 / 6 ? 0 : Math.min(0, dataMax + spread / 2)
  else high = dataMax + 0.05 * (dataMax - Math.min(low, dataMin))

  return [low, Math.max(high, low)]
}

function unitFor(low, high, intervals, fixedMin, fixedMax) {
  const range = Math.max(high - low, 1e-12)
  const exponent = Math.floor(Math.log10(range / intervals))
  for (let power = exponent - 1; power <= exponent + 2; power++) {
    for (const step of STEPS) {
      const unit = step * 10 ** power
      const min = fixedMin ?? floorTo(low, unit)
      const max = fixedMax ?? ceilTo(high, unit)
      if ((max - min) / unit <= intervals + 1e-9) return unit
    }
  }
  return 10 ** (exponent + 3)
}

/**
 * @returns {{ min: number, max: number, majorUnit: number }}
 */
export function resolveAxisScale(dataMin, dataMax, fixedMin, fixedMax, fixedMajor, axisLengthPt, labelSizePt) {
  fixedMin = fixedMin ?? null
  fixedMax = fixedMax ?? null
  fixedMajor = fixedMajor ?? null
  dataMin = dataMin ?? 0
  dataMax = dataMax ?? dataMin
  if (fixedMin !== null) dataMin = Math.min(dataMin, fixedMin)
  if (dataMax <= dataMin && fixedMax === null) dataMax = dataMin === 0 ? 1 : dataMin + Math.abs(dataMin)

  const intervals = Math.max(1, Math.min(MAX_INTERVALS, Math.floor(axisLengthPt / Math.max(1, labelSizePt * DENSITY))))
  const [low, high] = provisional(dataMin, dataMax, fixedMin, fixedMax)
  const unit = fixedMajor !== null && fixedMajor > 0 ? fixedMajor : unitFor(low, high, intervals, fixedMin, fixedMax)

  const min = fixedMin ?? floorTo(low, unit)
  let max = fixedMax ?? ceilTo(high, unit)
  if (fixedMax === null && max <= min) max = min + unit

  return { min: clean(min), max: clean(max), majorUnit: clean(unit) }
}
